import { createRng } from "@/lib/seededRandom";
import type { AIInsight } from "@/types";
import { CHURN_RISK_CLIENTS, TOP_EXPANSION_OPPORTUNITIES } from "./revenue";
import { DEPARTMENTS } from "./departments";
import { OPEN_ROLES } from "./employees";

const rng = createRng(667788);

function buildChurnInsights(): AIInsight[] {
  return CHURN_RISK_CLIENTS.slice(0, 5).map((c, i) => ({
    id: `insight-churn-${i}`,
    category: "churn-risk",
    title: `${c.name} shows strong signals of churn risk`,
    severity: c.riskLevel,
    confidence: rng.int(74, 95),
    whyItMatters: `Health score of ${c.healthScore} with ${c.openIssues} open issues represents $${(c.mrr * 12).toLocaleString()} in annual revenue at risk.`,
    recommendedAction: "Escalate to AM + OM for a joint save-play within 5 business days.",
    owner: c.assignedAM,
    dueDate: rng.daysFromNow(7, 2),
    relatedEntity: c.name,
    createdAt: rng.daysAgo(5),
  }));
}

function buildExpansionInsights(): AIInsight[] {
  return TOP_EXPANSION_OPPORTUNITIES.slice(0, 5).map((c, i) => ({
    id: `insight-expansion-${i}`,
    category: "expansion",
    title: `${c.name} is ready for an expansion conversation`,
    severity: "low",
    confidence: rng.int(70, 92),
    whyItMatters: `High health score (${c.healthScore}) and satisfaction (${c.satisfactionScore}) with room to grow — estimated $${(c.expansionValue ?? 0).toLocaleString()}/mo upside.`,
    recommendedAction: "AM to propose a second service line or additional VA seat this quarter.",
    owner: c.assignedAM,
    dueDate: rng.daysFromNow(21, 7),
    relatedEntity: c.name,
    createdAt: rng.daysAgo(8),
  }));
}

function buildDepartmentInsights(): AIInsight[] {
  return DEPARTMENTS.filter((d) => d.status === "at-risk").map((d, i) => ({
    id: `insight-dept-${i}`,
    category: "department-lag",
    title: `${d.name} is falling behind target this cycle`,
    severity: "high",
    confidence: rng.int(68, 88),
    whyItMatters: d.aiSummary,
    recommendedAction: d.actionItems[0] ?? "Review department scorecard with owner this week.",
    owner: d.owner,
    dueDate: rng.daysFromNow(10, 3),
    relatedEntity: d.name,
    createdAt: rng.daysAgo(4),
  }));
}

function buildCoachingInsights(): AIInsight[] {
  const names = ["Renata Cruz", "Isabella Ferreira", "Diego Villanueva"];
  return names.map((name, i) => ({
    id: `insight-coaching-${i}`,
    category: "coaching",
    title: `${name} may benefit from targeted coaching`,
    severity: "medium",
    confidence: rng.int(60, 85),
    whyItMatters: "Team performance metrics and escalation volume trending below peer benchmark for two review cycles.",
    recommendedAction: "Pair with a peer leader for a 30-day coaching plan.",
    owner: "People & Culture",
    dueDate: rng.daysFromNow(14, 5),
    relatedEntity: name,
    createdAt: rng.daysAgo(6 + i),
  }));
}

function buildHiringInsights(): AIInsight[] {
  const bottleneck = OPEN_ROLES.filter((r) => r.daysOpen > 45).slice(0, 3);
  return bottleneck.map((r, i) => ({
    id: `insight-hiring-${i}`,
    category: "hiring-bottleneck",
    title: `${r.title} (${r.department}) is a hiring bottleneck`,
    severity: r.priority,
    confidence: rng.int(72, 90),
    whyItMatters: `Open for ${r.daysOpen} days with only ${r.candidatesInPipeline} candidates in pipeline, delaying delivery capacity.`,
    recommendedAction: "Engage sourcing agency and expand geographic search radius.",
    owner: r.hiringManager,
    dueDate: rng.daysFromNow(10, 4),
    relatedEntity: r.department,
    createdAt: rng.daysAgo(3 + i),
  }));
}

const STRATEGIC_INSIGHTS: AIInsight[] = [
  {
    id: "insight-strategic-1",
    category: "strategic",
    title: "Standardize onboarding to cut time-to-value by 30%",
    severity: "medium",
    confidence: 81,
    whyItMatters: "Accounts onboarded via the ad-hoc playbook show 22% lower 90-day health scores than the standardized cohort.",
    recommendedAction: "Fund the Client Onboarding Process Redesign initiative to completion this quarter.",
    owner: "Diego Villanueva",
    dueDate: rng.daysFromNow(30, 10),
    createdAt: rng.daysAgo(9),
  },
  {
    id: "insight-strategic-2",
    category: "repeated-issue",
    title: "Invoice delivery delays recurring across 14 accounts",
    severity: "medium",
    confidence: 77,
    whyItMatters: "Same root cause (manual invoice generation) surfaced in 14 separate escalations over 60 days.",
    recommendedAction: "Prioritize the Automated Invoicing initiative's remaining rollout to affected accounts.",
    owner: "Andres Osei",
    dueDate: rng.daysFromNow(15, 5),
    createdAt: rng.daysAgo(11),
  },
  {
    id: "insight-strategic-3",
    category: "sentiment-change",
    title: "Client sentiment in Healthcare vertical dipped 9 points this month",
    severity: "high",
    confidence: 79,
    whyItMatters: "Aggregate sentiment across 34 Healthcare accounts dropped from 74 to 65, correlated with a bench capacity gap.",
    recommendedAction: "Prioritize cross-training for the Healthcare vertical bench shortage.",
    owner: "Renata Cruz",
    dueDate: rng.daysFromNow(12, 4),
    relatedEntity: "Healthcare",
    createdAt: rng.daysAgo(2),
  },
  {
    id: "insight-strategic-4",
    category: "ops-blind-spot",
    title: "12% of accounts have no logged contact in 30+ days",
    severity: "high",
    confidence: 84,
    whyItMatters: "Accounts without regular touchpoints are 3.4x more likely to churn within the next quarter based on historical pattern.",
    recommendedAction: "Enforce a minimum-touch cadence policy and alert AMs automatically.",
    owner: "Client Success",
    dueDate: rng.daysFromNow(9, 3),
    createdAt: rng.daysAgo(1),
  },
];

export const AI_INSIGHTS: AIInsight[] = [
  ...buildChurnInsights(),
  ...buildExpansionInsights(),
  ...buildDepartmentInsights(),
  ...buildCoachingInsights(),
  ...buildHiringInsights(),
  ...STRATEGIC_INSIGHTS,
].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
